import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { fetchQuestions, submitAnswersBatch, saveProgress, fetchProgress, deletePreviousAssessment } from '../../api/assessmentApi'; 
import { v4 as uuidv4 } from 'uuid';
import './Assessment.css';
import {
  FaSave,
} from "react-icons/fa";
import confetti from 'canvas-confetti';

// Remove console.logs in production
  if (process.env.NODE_ENV !== "development") {
    console.log = () => {};
  }

// Custom hook for managing local storage
const useLocalStorage = (userId) => {
  const getStorageKey = (key) => `subjectAssessment${key}_${userId}`;

  const getStoredData = (key, defaultValue = null) => {
    const value = localStorage.getItem(getStorageKey(key));
    if (!value) {
      return defaultValue;
    }
    try {
      return JSON.parse(value);
    } catch (err) {
      console.error(`Failed to parse JSON for key "${getStorageKey(key)}":`, err);
      console.error(`Stored value:`, value);
      return defaultValue;
    }
  };

  const setStoredData = (key, value) => {
    try {
      localStorage.setItem(getStorageKey(key), JSON.stringify(value));
    } catch (err) {
      console.error(`Failed to store data for key "${getStorageKey(key)}":`, err);
    }
  };

  const removeStoredData = (key) => {
    console.log(`Removing ${key} from localStorage`);
    localStorage.removeItem(getStorageKey(key));
  };

  return { getStoredData, setStoredData, removeStoredData };
};

// Custom hook for managing assessment state
const useAssessmentState = () => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [answeredOrder, setAnsweredOrder] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [answeredCountByCategory, setAnsweredCountByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastCompletionDate, setLastCompletionDate] = useState(null);
  const [hasProceededToPartII, setHasProceededToPartII] = useState(false);
  const [submittedAnswers, setSubmittedAnswers] = useState(new Set());
  const [sessionId, setSessionId] = useState(null);

  return {
    questions,
    setQuestions,
    currentIndex,
    setCurrentIndex,
    selectedAnswers,
    setSelectedAnswers,
    answeredOrder,
    setAnsweredOrder,
    answeredCount,
    setAnsweredCount,
    answeredCountByCategory,
    setAnsweredCountByCategory,
    loading,
    setLoading,
    error,
    setError,
    lastCompletionDate,
    setLastCompletionDate,
    hasProceededToPartII,
    setHasProceededToPartII,
    submittedAnswers,
    setSubmittedAnswers,
    sessionId,
    setSessionId,
  };
};

// Main component
const SubjectAssessment = ({ userId, isAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    questions,
    setQuestions,
    currentIndex,
    setCurrentIndex,
    selectedAnswers,
    setSelectedAnswers,
    answeredOrder,
    setAnsweredOrder,
    answeredCount,
    setAnsweredCount,
    answeredCountByCategory,
    setAnsweredCountByCategory,
    loading,
    setLoading,
    error,
    setError,
    lastCompletionDate,
    setLastCompletionDate,
    hasProceededToPartII,
    setHasProceededToPartII,
    submittedAnswers,
    setSubmittedAnswers,
    sessionId,
    setSessionId,
  } = useAssessmentState();
  const { getStoredData, setStoredData, removeStoredData } = useLocalStorage(userId);
  const [showIntro, setShowIntro] = useState(true);
  const [showBreakPage, setShowBreakPage] = useState(false);
  const [breakPageShown, setBreakPageShown] = useState(false);
  const [showPartICompletePage, setShowPartICompletePage] = useState(false);
  const [partICompleteShown, setPartICompleteShown] = useState(false);
  const [topSubcategoriesForPartII, setTopSubcategoriesForPartII] = useState([]);

const handleContinue = () => {
    setShowBreakPage(false);
    handleSaveProgress(); // optional but good practice
  };

  const handleSaveAndExit = () => {
    handleSaveProgress();
    navigate('/dashboard', { state: { fromSubjectScores: true, sessionId } });
  };

  
const handleContinueToPartII = () => {
  setShowPartICompletePage(false);
  // Optional: extra save
  handleSaveProgress();
};

const handleSaveAndExitFromPartIComplete = () => {
  handleSaveProgress();
  navigate('/dashboard', { state: { fromSubjectScores: true, sessionId } });
};


    // Track page views when the component mounts or route changes
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-B8RJQ1KWE4', {
        page_path: location.pathname,
        page_title: 'Subject Assessment',
      });
    }
  }, [location]);
  
  // Check if user can take the assessment
  const canTakeAssessment = () => {
    if (isAdmin) return true;
    if (!lastCompletionDate) return true;
    try {
      const sixMonthsInMs = 6 * 30 * 24 * 60 * 60 * 1000;
      const timeSinceLast = Date.now() - new Date(lastCompletionDate).getTime();
      return timeSinceLast >= sixMonthsInMs;
    } catch (err) {
      console.error('Invalid lastCompletionDate:', lastCompletionDate);
      return true;
    }
  };

  // Load initial assessment data
  useEffect(() => {
    let mounted = true;

    const loadAssessment = async () => {
      if (!mounted) return;

      // Load stored data from localStorage
      const storedCompletion = getStoredData('Completion');
      let storedSessionId = getStoredData('SessionId');
      let initialAnswers = getStoredData('Answers', {});
      let initialOrder = getStoredData('Order', []);
      let initialQuestions = getStoredData('Questions', []);
      let initialIndex = getStoredData('Index', 0);
      let initialSubcategories = getStoredData('TopSubcategories', []);
      let initialPartII = getStoredData('HasProceededToPartII', false);
      let newSession = false;

      console.log('Initial stored data:', {
        storedCompletion,
        storedSessionId,
        answersCount: Object.keys(initialAnswers).length,
        orderLength: initialOrder.length,
        questionsLength: initialQuestions.length,
        initialIndex,
        initialSubcategories,
        initialPartII,
      });

      setLastCompletionDate(storedCompletion);

      // Validate session
      const canRetake = canTakeAssessment();
      const hasLocalProgress =
        storedSessionId &&
        Object.keys(initialAnswers).length > 0 &&
        Array.isArray(initialOrder) &&
        Array.isArray(initialQuestions);

      console.log('Session checks:', { isAdmin, canRetake, storedCompletion, hasLocalProgress });

      if ((isAdmin && !hasLocalProgress) || (!isAdmin && canRetake && storedCompletion)) {
        console.log('Starting new session: admin with no progress or completed assessment');
        storedSessionId = uuidv4();
        newSession = true;
        removeStoredData('Answers');
        removeStoredData('Order');
        removeStoredData('Questions');
        removeStoredData('Index');
        removeStoredData('TopSubcategories');
        removeStoredData('HasProceededToPartII');
        removeStoredData('SessionId');
        removeStoredData('Completion');
        initialAnswers = {};
        initialOrder = [];
        initialQuestions = [];
        initialIndex = 0;
        initialSubcategories = [];
        initialPartII = false;
      } else if (hasLocalProgress && !storedCompletion) {
        console.log('Resuming existing incomplete session');
        newSession = false;
      } else {
        console.log('No valid local progress or starting new session');
        storedSessionId = uuidv4();
        newSession = true;
        removeStoredData('Answers');
        removeStoredData('Order');
        removeStoredData('Questions');
        removeStoredData('Index');
        removeStoredData('TopSubcategories');
        removeStoredData('HasProceededToPartII');
        removeStoredData('SessionId');
        initialAnswers = {};
        initialOrder = [];
        initialQuestions = [];
        initialIndex = 0;
        initialSubcategories = [];
        initialPartII = false;
      }

      setSessionId(storedSessionId);
      setStoredData('SessionId', storedSessionId);

      // Fetch questions
      try {
        const data = await fetchQuestions({ status: 'Subject Assessments' });
        if (!data || data.length === 0) {
          throw new Error('No questions returned from the server.');
        }

        const uniqueQuestions = Array.from(new Map(data.map((q) => [q._id, q])).values()).filter(
          (q) => q._id && q.questionText && Array.isArray(q.options) && q.options.length > 0
        );
        if (uniqueQuestions.length === 0) {
          throw new Error('No valid questions with options found.');
        }

        let questionsToSet = [];
        const partIQuestions = uniqueQuestions.filter((q) => q.category === 'Part I');
        const partIIQuestions = uniqueQuestions.filter((q) => q.category === 'Part II');

        if (!newSession && initialPartII && initialSubcategories.length > 0) {
          const topsubCategoryTaxonomies = initialSubcategories
            .map((sub) => {
              const question = partIQuestions.find((q) => q.subCategory === sub);
              return question && question.taxonomy ? question.taxonomy : [];
            })
            .flat()
            .filter(Boolean);

          let filteredPartIIQuestions = partIIQuestions.filter((q) =>
            q.subCategory && topsubCategoryTaxonomies.includes(q.subCategory)
          );

          const partIIAnsweredIds = initialOrder.filter((id) =>
            filteredPartIIQuestions.some((q) => q._id === id)
          );
          const partIIAnsweredQuestions = partIIAnsweredIds
            .map((id) => filteredPartIIQuestions.find((q) => q._id === id))
            .filter((q) => q);
          const partIIUnanswered = filteredPartIIQuestions
            .filter((q) => !partIIAnsweredIds.includes(q._id))
            .sort(() => Math.random() - 0.5);
          questionsToSet = [...partIIAnsweredQuestions, ...partIIUnanswered];
          setStoredData('Questions', questionsToSet.map((q) => q._id));
        } else if (!newSession && initialQuestions.length > 0) {
          questionsToSet = initialQuestions
            .map((id) => uniqueQuestions.find((q) => q._id === id))
            .filter((q) => q);
          if (questionsToSet.length === 0) {
            console.warn('Stored questions invalid, resetting.');
          }
        }

        if (questionsToSet.length === 0) {
          if (partIQuestions.length === 0) {
            throw new Error('No Part I questions available.');
          }
          questionsToSet = [...partIQuestions.sort(() => Math.random() - 0.5)];
          setStoredData('Questions', questionsToSet.map((q) => q._id));
          setStoredData('TopSubcategories', initialSubcategories);
          setStoredData('HasProceededToPartII', false);
        }

        initialOrder = initialOrder.filter((id) => questionsToSet.some((q) => q._id === id));

        const hasPartIIQuestions = questionsToSet.some((q) => q.category === 'Part II');
        if (hasPartIIQuestions && !initialPartII) {
          console.warn('Detected Part II questions, setting hasProceededToPartII to true');
          initialPartII = true;
        }

        if (mounted) {
          setQuestions(questionsToSet);
          setSelectedAnswers(initialAnswers);
          setAnsweredOrder(initialOrder);
          const newAnsweredCount = questionsToSet.filter(
            (q) => initialAnswers[q._id] !== undefined
          ).length;
          setAnsweredCount(newAnsweredCount);
          setHasProceededToPartII(initialPartII);

          let startIndex = initialIndex;
          if (initialPartII) {
            const partIIAnswered = initialOrder.filter((id) =>
              questionsToSet.some((q) => q._id === id && q.category === 'Part II')
            );
            if (partIIAnswered.length > 0) {
              const lastPartIIAnsweredId = partIIAnswered[partIIAnswered.length - 1];
              const lastPartIIIndex = questionsToSet.findIndex(
                (q) => q._id === lastPartIIAnsweredId
              );
              startIndex = lastPartIIIndex >= 0 ? lastPartIIIndex : 0;
            } else {
              startIndex = 0;
            }
          } else if (initialOrder.length > 0) {
            const lastAnsweredId = initialOrder[initialOrder.length - 1];
            const lastAnsweredIndex = questionsToSet.findIndex(
              (q) => q._id === lastAnsweredId
            );
            startIndex = lastAnsweredIndex >= 0 ? lastAnsweredIndex : initialIndex;
          }
          startIndex = Math.min(Math.max(startIndex, 0), questionsToSet.length - 1);
          setCurrentIndex(startIndex);

          const categoryCounts = {};
          questionsToSet.forEach((q) => {
            if (initialAnswers[q._id]) {
              categoryCounts[q.category] = (categoryCounts[q.category] || 0) + 1;
            }
          });
          setAnsweredCountByCategory(categoryCounts);

          setStoredData('Answers', initialAnswers);
          setStoredData('Order', initialOrder);
          setStoredData('Index', startIndex);
          setStoredData('TopSubcategories', initialSubcategories);
          setStoredData('HasProceededToPartII', initialPartII);

          setLoading(false);
          console.log('Loaded assessment:', {
            sessionId: storedSessionId,
            questionsToSet: questionsToSet.map((q) => q._id),
            answeredOrder: initialOrder,
            currentIndex: startIndex,
            hasProceededToPartII: initialPartII,
            selectedAnswers: initialAnswers,
            answeredCount: newAnsweredCount,
            allAnswered: questionsToSet.every((q) => initialAnswers[q._id] !== undefined),
          });
        }
      } catch (err) {
        console.error('Error loading questions:', err.message);
        if (mounted) {
          setError(
            `Failed to load assessment: ${err.message}. Please contact support or try again later.`
          );
          setLoading(false);
        }
      }
    };

  loadAssessment();

  // Add classes when component mounts
  document.body.classList.add('specific-page-subject');

  // Cleanup: remove BOTH classes when component unmounts
  return () => {
    mounted = false;
    document.body.classList.remove('specific-page-subject');
    document.body.classList.remove('specific-page-intro-screen');
  };
}, [userId, isAdmin, navigate]);

useEffect(() => {
  if (!sessionId) return; // wait until sessionId is set

  const introKey = `IntroShown_${sessionId}`;
  const introAlreadyShown = getStoredData(introKey, false);

  // Skip intro if:
  // - already shown in this session, OR
  // - there are already answers (resumed progress)
  if (introAlreadyShown || answeredCount > 0) {
    setShowIntro(false);
  } else {
    setShowIntro(true);
  }
}, [sessionId, answeredCount, getStoredData]);


  useEffect(() => {
  if (
    !hasProceededToPartII &&
    !showIntro &&
    !showBreakPage &&
    !breakPageShown &&
    answeredCount === 39   // ← confirm this number makes sense for your test
  ) {
    console.log("Break page triggered — starting confetti"); // ← debug

    setShowBreakPage(true);
    setBreakPageShown(true);

    // Start confetti immediately (no need for interval if you want one burst only)
    const shootConfetti = () => {
      
      // Center burst
      confetti({
        particleCount: 20,
        spread: 90,
        startVelocity: 30,
        decay: 0.91,
        gravity: 0.5,
        origin: { x: 0.5, y: 0.65 },
        shapes: ['star', 'circle'],
        scalar: 1.4,
        ticks: 300,
      });
    };

    // Fire multiple bursts for celebration feel
    shootConfetti();
    setTimeout(shootConfetti, 600);
    setTimeout(shootConfetti, 1200);
    setTimeout(shootConfetti, 2000);

  }
}, [answeredCount, hasProceededToPartII, showIntro, showBreakPage, breakPageShown]);

// ────────────────────────────────────────────────
// Add to body class useEffect (so celebration page also gets full-screen style)
// ────────────────────────────────────────────────
useEffect(() => {
  if (showIntro || showBreakPage || showPartICompletePage) {
    document.body.classList.add('specific-page-intro-screen');
  } else {
    document.body.classList.remove('specific-page-intro-screen');
  }

  return () => {
    document.body.classList.remove('specific-page-intro-screen');
  };
}, [showIntro, showBreakPage, showPartICompletePage]);


 
  // Save progress to local storage
  const handleSaveProgress = () => {
    if (!userId || !sessionId) {
      alert('User ID or session ID is missing. Please log in.');
      return;
    }

    if (lastCompletionDate) {
      console.log('Assessment already completed, skipping save progress');
      alert('Assessment is already completed. No progress to save.');
      return;
    }

    if (answeredCount === 0) {
      console.log('No answers to save');
      alert('No answers provided to save.');
      return;
    }

    try {
      // Validate data before saving
      const answersToSave = { ...selectedAnswers };
      Object.keys(answersToSave).forEach((key) => {
        if (typeof answersToSave[key] !== 'string') {
          console.warn(`Invalid answer for question ${key}:`, answersToSave[key]);
          answersToSave[key] = String(answersToSave[key]);
        }
      });

      setStoredData('SessionId', sessionId);
      setStoredData('Answers', answersToSave);
      setStoredData('Order', answeredOrder);
      setStoredData('Questions', questions.map((q) => q._id));
      setStoredData('Index', currentIndex);
      setStoredData('TopSubcategories', getStoredData('TopSubcategories', []));
      setStoredData('HasProceededToPartII', hasProceededToPartII);

      // Verify storage
      console.log('Verifying saved data:');
      console.log('SessionId:', getStoredData('SessionId'));
      console.log('Answers:', getStoredData('Answers'));
      console.log('Order:', getStoredData('Order'));
      console.log('Questions:', getStoredData('Questions'));
      console.log('Index:', getStoredData('Index'));
      console.log('TopSubcategories:', getStoredData('TopSubcategories'));
      console.log('HasProceededToPartII:', getStoredData('HasProceededToPartII'));

      alert('Progress saved locally!');
      setError('');
    } catch (err) {
      console.error('Error saving progress locally:', err);
      alert(`Failed to save progress: ${err.message}`);
    }
  };

  // Return to dashboard
  const handleReturnToDashboard = () => {
    if (answeredCount > 0) {
      handleSaveProgress();
    }
    navigate('/dashboard', { state: { fromSubjectScores: true, sessionId } });
  };

  // Submit all answers
const handleAnswerSubmit = async () => {
  const allAnswered = questions.every((q) => selectedAnswers[q._id] !== undefined);
  if (!allAnswered) {
    alert('Please complete all questions before submitting.');
    return;
  }

  const warningMessage = isAdmin
    ? 'UniReady Assessment completed! Retake anytime.'
    : 'UniReady Assessment completed! Retake available in 6 months.';
  alert(warningMessage);

  if (!isAdmin) {
    const confirmationMessage = 'Finalise submission? This action cannot be undone.';
    const confirmSubmit = window.confirm(confirmationMessage);
    if (!confirmSubmit) return;
  }

  // Track the "Submit" event in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'submit_assessment', {
        event_category: 'SubjectAssessment',
        event_label: 'Assessment Submission',
        value: answeredCount,
        session_id: sessionId,
        user_id: userId,
      });
    }

  try {
    if (!userId) {
      throw new Error('User ID is missing. Please log in.');
    }
    if (!sessionId) {
      throw new Error('Session ID is missing. Please restart the assessment.');
    }

    console.log('Submitting answers with:', { userId, sessionId, assessmentType: 'Subject', answersCount: questions.length });

    // Step 1: Check for prior completion
    let hasPriorData = false;
    try {
      const progress = await fetchProgress(userId, { assessmentType: 'Subject', fetchLatest: true });
      hasPriorData = progress && progress.isCompleted;
      console.log('Prior progress check:', { hasPriorData, lastCompletionDate: progress?.lastCompletionDate });
    } catch (err) {
      console.warn('Error checking prior progress:', err.message);
    }

    // Step 2: Delete previous assessment data for both Subject and SubjectPart3 if it exists
    if (hasPriorData || isAdmin) {
      try {
        // Delete Subject assessment data
        const deleteResultSubject = await deletePreviousAssessment(userId, 'Subject');
        console.log('Delete Subject result:', deleteResultSubject);
        if (deleteResultSubject.message === 'No previous assessment data found') {
          if (hasPriorData) {
            console.error('Unexpected: No Subject data deleted despite prior data existing');
            throw new Error('Failed to delete prior Subject data despite existing records');
          }
          console.log('No previous Subject assessment data found, proceeding with submission');
        } else if (deleteResultSubject.message === 'Previous assessment data deleted successfully') {
          console.log('Previous Subject assessment data deleted:', {
            userId,
            deletedAnswers: deleteResultSubject.deletedAnswers,
            deletedProgresses: deleteResultSubject.deletedProgresses,
          });
        } else {
          throw new Error('Unexpected Subject deletion response: ' + JSON.stringify(deleteResultSubject));
        }

        // Delete SubjectPart3 assessment data
        try {
          const deleteResultPart3 = await deletePreviousAssessment(userId, 'SubjectPart3');
          console.log('Delete SubjectPart3 result:', deleteResultPart3);
          if (deleteResultPart3.message === 'No previous assessment data found') {
            console.log('No previous SubjectPart3 assessment data found, proceeding with submission');
          } else if (deleteResultPart3.message === 'Previous assessment data deleted successfully') {
            console.log('Previous SubjectPart3 assessment data deleted:', {
              userId,
              deletedAnswers: deleteResultPart3.deletedAnswers,
              deletedProgresses: deleteResultPart3.deletedProgresses,
            });
          } else {
            throw new Error('Unexpected SubjectPart3 deletion response: ' + JSON.stringify(deleteResultPart3));
          }
        } catch (err) {
          console.warn('Non-critical error deleting SubjectPart3 assessment data:', err.message);
          // Continue with submission even if SubjectPart3 deletion fails, as it's not critical
        }
      } catch (err) {
        console.error('Error deleting previous assessment data:', err);
        throw new Error('Failed to clear previous Subject assessment data: ' + err.message);
      }
    } else {
      console.log('First attempt detected, no deletion needed for Subject');
      // Still attempt to delete SubjectPart3 data to ensure clean state
      try {
        const deleteResultPart3 = await deletePreviousAssessment(userId, 'SubjectPart3');
        console.log('Delete SubjectPart3 result:', deleteResultPart3);
        if (deleteResultPart3.message === 'No previous assessment data found') {
          console.log('No previous SubjectPart3 assessment data found');
        } else if (deleteResultPart3.message === 'Previous assessment data deleted successfully') {
          console.log('Previous SubjectPart3 assessment data deleted:', {
            userId,
            deletedAnswers: deleteResultPart3.deletedAnswers,
            deletedProgresses: deleteResultPart3.deletedProgresses,
          });
        } else {
          console.warn('Unexpected SubjectPart3 deletion response:', deleteResultPart3);
        }
      } catch (err) {
        console.warn('Non-critical error deleting SubjectPart3 assessment data:', err.message);
        // Continue with submission
      }
    }

  const computeTopPartIISubjects = () => {
  const subjectScores = {};

  questions.forEach(q => {
    if (!q.subCategory) return;
    const answerText = selectedAnswers[q._id];
    if (!answerText || !Array.isArray(q.options)) return;

    const selectedOption = q.options.find(opt => opt.optionText === answerText);
    const score = selectedOption ? selectedOption.score : 0;

    if (!subjectScores[q.subCategory]) {
      subjectScores[q.subCategory] = { total: 0, count: 0 };
    }
    subjectScores[q.subCategory].total += score;
    subjectScores[q.subCategory].count += 1;
  });

  const sorted = Object.entries(subjectScores)
    .map(([subject, data]) => ({
      subject,
      averageScore: Number((data.total / data.count).toFixed(2))
    }))
    .sort((a, b) => b.averageScore - a.averageScore);

  if (sorted.length === 0) return [];
  if (sorted.length <= 3) return sorted;

  // Get the score of the 3rd highest subject
  const thirdHighestScore = sorted[2].averageScore;

  // Include ALL subjects that match or beat the 3rd highest score
  return sorted.filter(item => item.averageScore >= thirdHighestScore);
};

    // Step 3: Prepare and submit new answers
    const questionOptionsMap = questions.reduce((map, q) => ({
      ...map,
      [q._id]: { options: q.options },
    }), {});
    const answersBatch = questions
      .filter((question) => !submittedAnswers.has(question._id))
      .map((question) => {
        const answer = selectedAnswers[question._id];
        if (!answer) {
          throw new Error(`No answer provided for question ${question._id}`);
        }
        const selectedOption = Array.isArray(question.options)
          ? question.options.find((opt) => opt.optionText === answer)
          : null;
        const score = selectedOption ? selectedOption.score : 0;
        return {
          userId,
          questionId: question._id,
          sessionId,
          category: question.category || 'Part II',
          subCategory: question.subCategory || null,
          facet: question.facet || null,
          taxonomy: question.taxonomy || null,
          score,
          assessmentType: 'Subject',
        };
      });

    if (answersBatch.length > 0) {
      await submitAnswersBatch(userId, sessionId, answersBatch, 'Subject', questionOptionsMap);
      answersBatch.forEach((answer) => {
        setSubmittedAnswers((prev) => new Set(prev).add(answer.questionId));
      });
    } else {
      console.log('No new answers to submit. All questions may already be submitted.');
    }

    // Step 4: Save completion data to database
    const completionDate = new Date().toISOString();
    const progressData = {
      userId,
      sessionId,
      assessmentType: 'Subject',
      topSubcategories: getStoredData('TopSubcategories', []),
      lastCompletionDate: completionDate,
    };

    console.log('Saving completion data:', progressData);
    await saveProgress(progressData);

    // Step 5: Update local storage and state
    setStoredData('Completion', completionDate);
    setLastCompletionDate(completionDate);

    removeStoredData('Answers');
    removeStoredData('Order');
    removeStoredData('Questions');
    removeStoredData('Index');
    removeStoredData('TopSubcategories');
    removeStoredData('HasProceededToPartII');
    removeStoredData('SessionId');
    
     // Clear all SubjectPart3-related keys
  Object.keys(localStorage)
    .filter(key => 
      key.startsWith('subjectPart3Assessment') || 
      key.startsWith(`raw_answers_${userId}_SubjectPart3`) ||
      key.startsWith(`progress_${userId}_SubjectPart3`) ||
      key.startsWith(`visualizations_${userId}_SubjectPart3`) ||
      key.startsWith(`subjectpart3SessionId`)
    )
    .forEach(key => {
      console.log(`Removing key: ${key}`);
      localStorage.removeItem(key);
    });

  // Clear sessionStorage
  sessionStorage.removeItem(`visualizations_${userId}_Subject`);
  sessionStorage.removeItem(`raw_answers_${userId}_Subject`);
  sessionStorage.removeItem(`visualizations_${userId}_SubjectPart3`);
  sessionStorage.removeItem(`raw_answers_${userId}_SubjectPart3`);
  console.log('localStorage and sessionStorage cleanup complete');

    // Step 6: Reset state for admin users
      if (isAdmin) {
      setSelectedAnswers({});
      setAnsweredOrder([]);
      setAnsweredCount(0);
      setCurrentIndex(0);
      setHasProceededToPartII(false);
      setSessionId(uuidv4());
      removeStoredData('Completion');
    }

    // Navigate back with BOTH flags for maximum compatibility
     const topPartII = computeTopPartIISubjects();

    navigate('/dashboard', { 
      replace: true,
      state: { 
        justSubmittedSubject: true,           // Main flag
        justSubmittedUniReady: true,          // For celebration modal
        completionDate: completionDate,
        sessionId: sessionId,
        topPartIISubjects: topPartII          // ← Important for dashboard save
      } 
    });
  } catch (err) {
    console.error('Error submitting answers:', {
      message: err.message,
      stack: err.stack,
    });
    const errorMessage =
      err.message.includes('Answer already submitted') ||
      err.message.includes('You can only complete this assessment once every 6 months')
        ? 'This assessment has already been completed. You can retake it in 6 months.'
        : err.message.includes('log in') || err.message.includes('User ID is missing')
        ? 'Session expired or invalid credentials. Please log in again.'
        : err.message.includes('Failed to clear previous assessment data')
        ? 'Unable to connect to the server to clear previous data. Please check your connection and try again.'
        : `Failed to submit answers: ${err.message}`;

        // Track submission failure
      if (window.gtag) {
        window.gtag('event', 'submit_assessment_failure', {
          event_category: 'SubjectAssessment',
          event_label: 'Assessment Submission Failed',
          value: answeredCount,
          session_id: sessionId,
          user_id: userId,
          error_message: err.message,
        });
      }
      
    alert(errorMessage);
    if (err.message.includes('log in') || err.message.includes('User ID is missing')) {
      navigate('/login');
    } else {
      navigate('/dashboard');
    }
  }
};


  // Navigate to next question
  const handleNext = () => {
    const currentQuestionId = questions[currentIndex]._id;
    const currentAnswer = selectedAnswers[currentQuestionId];

    if (currentAnswer === undefined || currentAnswer === '') {
      alert('Please select an answer before proceeding.');
      return;
    }

    const currentOrderIndex = answeredOrder.indexOf(currentQuestionId);
    if (currentOrderIndex >= 0 && currentOrderIndex < answeredOrder.length - 1) {
      const nextAnsweredId = answeredOrder[currentOrderIndex + 1];
      const nextAnsweredIndex = questions.findIndex((q) => q._id === nextAnsweredId);
      if (nextAnsweredIndex >= 0) {
        setCurrentIndex(nextAnsweredIndex);
        return;
      }
    }

    let nextIndex = currentIndex + 1;
    while (nextIndex < questions.length && selectedAnswers[questions[nextIndex]._id]) {
      nextIndex++;
    }

    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
    } else if (answeredOrder.length > 0 && currentOrderIndex < answeredOrder.length - 1) {
      const nextAnsweredId = answeredOrder[currentOrderIndex + 1];
      const nextAnsweredIndex = questions.findIndex((q) => q._id === nextAnsweredId);
      if (nextAnsweredIndex >= 0) {
        setCurrentIndex(nextAnsweredIndex);
      }
    }
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (currentIndex === 0) return;

    const currentQuestionId = questions[currentIndex]._id;
    const currentOrderIndex = answeredOrder.indexOf(currentQuestionId);

    // If the current question has already been answered, retrace the
    // user's actual answering path via answeredOrder (handles Part II
    // questions that may have been answered out of sequential order).
    if (currentOrderIndex > 0) {
      const prevAnsweredId = answeredOrder[currentOrderIndex - 1];
      const prevQuestionIndex = questions.findIndex((q) => q._id === prevAnsweredId);
      if (prevQuestionIndex >= 0) {
        setCurrentIndex(prevQuestionIndex);
        return;
      }
    }

    // Current question hasn't been answered yet — e.g. the user was just
    // auto-advanced onto it — so it won't be in answeredOrder. In that
    // case just step back one position in the question list rather than
    // doing nothing.
    setCurrentIndex(currentIndex - 1);
  };

 const handleStartAssessment = () => {
  setShowIntro(false);

  if (sessionId) {
    const introKey = `IntroShown_${sessionId}`;
    setStoredData(introKey, true);           // Mark intro as shown for this session
  }
};

  if (loading) return <p>Loading UniReady Assessment questions...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!questions.length) {
    return (
      <p className="error-message">
        No UniReady Assessment questions available. Please contact support.
      </p>
    );
  }

  // ────────────────────────────────────────────────
  //  NEW: Intro screen
  // ────────────────────────────────────────────────
    if (showIntro) {
    return (
      <div className="specific-page-intro-screen">
        <div className="intro-content">
          <div className="admin-dashboard-logo intro-screen"></div>

          <h2 className="intro-heading">Welcome to the ASPIRE UniReady Psychometric Test</h2>
          <p className="copyright intro-copyright">
            © 2026 Oxbridge Gateway Ltd., All Rights Reserved.
          </p>

          <p className="intro-text">
            Most students complete this 2 sessions assessment in about <strong>25–30 minutes</strong>
        <br></br>
      <h3 style={{marginTop: '10px'}}>You can exit at any time during the assessment — your progress will be automatically saved locally.</h3></p>

          <button 
            className="start-assessment-button nav-button" 
            onClick={handleStartAssessment}
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }
  
   if (showBreakPage) {
    return (
      <div className="specific-page-intro-screen">
        <div className="intro-content break-content">
          <div className="admin-dashboard-logo intro-screen"></div>

          <h2 className="intro-heading-great-job">Great Progress!</h2>
          
          <p className="intro-text break-text">
            You’re now <strong>HALFWAY</strong> through the questions in Part I<br />
            <strong>Take a break!</strong><br />
            You can come back anytime to continue.
          </p>

          <div style={{marginTop: '100px'}} className="break-buttons">
            <button 
              className="nav-button continue-button"
              onClick={handleContinue}
            >
              Continue Now
            </button>

            <button 
              className="nav-button save-exit-button"
              onClick={handleSaveAndExit}
            >
              Save & Exit
            </button>
          </div>

        </div>
      </div>
    );
  }

if (showPartICompletePage) {
  return (
    <div className="specific-page-intro-screen">
      <div className="intro-content break-content">
        <div className="admin-dashboard-logo intro-screen"></div>

        <h2 className="intro-heading-great-job">Fantastic Work!</h2>

 <div className="intro-text break-text" style={{ marginBottom: '50px'}}>
        <p  style={{ marginBottom: '1.5rem' }}>
          You’ve completed <strong>Part I</strong><br />
          Based on your answers, Part II will focus on the following areas:
        </p>

          {topSubcategoriesForPartII.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, fontSize: '1.3rem', lineHeight: '2.1' }}>
              {topSubcategoriesForPartII.map((sub, index) => (
                <li key={index} style={{ color: '#ffaa00', margin: '0.6rem 0', fontWeight: 500 }}>
                  <strong>{sub}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ fontStyle: 'italic', opacity: 0.9 }}>
              Subjects selected based on your strongest areas
            </p>
          )}
        </div>

        <div className="break-buttons">
          <button
            className="nav-button continue-button"
            onClick={handleContinueToPartII}
          >
            Start Part II
          </button>

          <button
            className="nav-button save-exit-button"
            onClick={handleSaveAndExitFromPartIComplete}
          >
            Save & Exit
          </button>
        </div>
      </div>
    </div>
  );
}

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) {
      console.error('No current question available');
      setError('No question available. Please try again.');
      return;
    }

    const wasPreviouslyAnswered = !!selectedAnswers[currentQuestion._id];

    setSelectedAnswers((prev) => {
      const newAnswers = { ...prev, [currentQuestion._id]: answer };
      console.log('Updated selectedAnswers:', newAnswers);
      return newAnswers;
    });

    // Track the answered order locally too, since setAnsweredOrder's
    // updater callback below runs async and we need the up-to-date
    // order immediately for auto-advance.
    let orderForAdvance = answeredOrder.includes(currentQuestion._id)
      ? answeredOrder
      : [...answeredOrder, currentQuestion._id];

    if (!wasPreviouslyAnswered) {
      setAnsweredCount((prev) => {
        const newCount = prev + 1;
        console.log('Updated answeredCount:', newCount);
        return newCount;
      });
      setAnsweredOrder((prev) => {
        if (!prev.includes(currentQuestion._id)) {
          const newOrder = [...prev, currentQuestion._id];
          console.log('Updated answeredOrder:', newOrder);
          return newOrder;
        }
        return prev;
      });
      setAnsweredCountByCategory((prev) => {
        const category = currentQuestion.category || (hasProceededToPartII ? 'Part II' : 'Part I');
        const newCounts = { ...prev, [category]: (prev[category] || 0) + 1 };
        console.log('Updated answeredCountByCategory:', newCounts);
        return newCounts;
      });
    }

    // Save answer to local storage
    try {
      setStoredData('Answers', { ...selectedAnswers, [currentQuestion._id]: answer });
      setStoredData('Order', orderForAdvance);
      setStoredData('Index', currentIndex);
    } catch (err) {
      console.error('Error saving answer to local storage:', err);
    }

    // Auto-advance to the next question after a brief pause so the
    // selection highlight is visible before the view moves on. Skipped
    // when this is the last question so the Proceed/Submit button shows.
    if (currentIndex < questions.length - 1) {
      setTimeout(() => {
        advanceToNextQuestion(currentQuestion._id, orderForAdvance);
      }, 300);
    }
  };

  // Advance to the next question, mirroring handleNext's logic but driven
  // directly by the answer that was just selected rather than waiting on
  // React state (selectedAnswers/answeredOrder) to finish updating.
  const advanceToNextQuestion = (currentQuestionId, orderAfterAnswer) => {
    const currentOrderIndex = orderAfterAnswer.indexOf(currentQuestionId);
    if (currentOrderIndex >= 0 && currentOrderIndex < orderAfterAnswer.length - 1) {
      const nextAnsweredId = orderAfterAnswer[currentOrderIndex + 1];
      const nextAnsweredIndex = questions.findIndex((q) => q._id === nextAnsweredId);
      if (nextAnsweredIndex >= 0) {
        setCurrentIndex(nextAnsweredIndex);
        return;
      }
    }

    const currentQuestionIndex = questions.findIndex((q) => q._id === currentQuestionId);
    let nextIndex = currentQuestionIndex + 1;
    while (
      nextIndex < questions.length &&
      selectedAnswers[questions[nextIndex]._id] !== undefined &&
      questions[nextIndex]._id !== currentQuestionId
    ) {
      nextIndex++;
    }

    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
    }
  };

 // Proceed to Part II
const handleSaveAndProceed = async () => {
  const partIQuestions = (await fetchQuestions({ status: 'Subject Assessments' })).filter(
    (q) => q.category === 'Part I'
  );
  const partIAnswered = partIQuestions.every((q) => selectedAnswers[q._id] !== undefined);

  if (!partIAnswered) {
    alert('Please complete all Part I questions before proceeding.');
    return;
  }

  const confirmProceed = window.confirm(
    'Warning: Once you start Part II, you cannot return to Part I. You may retake the assessment in 6 months. Proceed?'
  );
  if (!confirmProceed) return;

  // ─── Existing score calculation logic remains unchanged ───
  const subCategoryScores = {};
  const subjectScoresBySubCategory = {};

  partIQuestions.forEach((q) => {
    const answerText = selectedAnswers[q._id];
    if (answerText && Array.isArray(q.options)) {
      const selectedOption = q.options.find((opt) => opt.optionText === answerText);
      const score = selectedOption ? selectedOption.score : 0;
      if (q.subCategory) {
        subCategoryScores[q.subCategory] = (subCategoryScores[q.subCategory] || 0) + score;
        // Track scores by subject in facet field
        if (q.facet && (q.facet !== 'All' || q.facet === 'All')) {
          if (!subjectScoresBySubCategory[q.subCategory]) {
            subjectScoresBySubCategory[q.subCategory] = {};
          }
          if (q.facet !== 'All') {
            subjectScoresBySubCategory[q.subCategory][q.facet] =
              (subjectScoresBySubCategory[q.subCategory][q.facet] || 0) + score;
          }
          // Include 'All' facet scores in all subjects under the same subCategory
          if (q.facet === 'All') {
            const question = partIQuestions.find(
              (pq) => pq.subCategory === q.subCategory && pq.facet !== 'All'
            );
            if (question && question.taxonomy) {
              question.taxonomy.forEach((subject) => {
                subjectScoresBySubCategory[q.subCategory][subject] =
                  (subjectScoresBySubCategory[q.subCategory][subject] || 0) + score;
              });
            }
          }
        }
      }
    }
  });

  // Identify top subCategories (existing logic)
const sortedSubcategories = Object.entries(subCategoryScores).sort(([, a], [, b]) => b - a);
  let topSubcategories = [];
  if (sortedSubcategories.length > 0) {
    topSubcategories.push(sortedSubcategories[0][0]);
    if (sortedSubcategories.length > 1) {
      const secondHighestScore = sortedSubcategories[1][1];
      topSubcategories = topSubcategories.concat(
        sortedSubcategories
          .slice(1)
          .filter(([, score]) => score >= secondHighestScore)
          .map(([subCategory]) => subCategory)
      );
    }
  }

    setTopSubcategoriesForPartII(topSubcategories);

  // Identify subjects with total score >= 10
  const eligibleSubjects = new Set();
  Object.entries(subjectScoresBySubCategory).forEach(([subCategory, subjects]) => {
    Object.entries(subjects).forEach(([subject, score]) => {
      if (score >= 10) {
        eligibleSubjects.add(subject);
      }
    });
  });

  if (topSubcategories.length === 0 && eligibleSubjects.size === 0) {
    setError(
      'Failed to calculate subcategories or eligible subjects. Please ensure all Part I questions are answered correctly.'
    );
    return;
  }

  // Get taxonomies for top subCategories
  const topsubCategoryTaxonomies = topSubcategories
    .map((sub) => {
      const question = partIQuestions.find((q) => q.subCategory === sub);
      return question && question.taxonomy ? question.taxonomy : [];
    })
    .flat()
    .filter(Boolean);

  // Combine taxonomies with eligible subjects
  const allEligibleTaxonomies = [...new Set([...topsubCategoryTaxonomies, ...eligibleSubjects])];

  let availablePartIIQuestions = [];
  try {
    const data = await fetchQuestions({ status: 'Subject Assessments' });
    const uniqueQuestions = Array.from(new Map(data.map((q) => [q._id, q])).values());
    availablePartIIQuestions = uniqueQuestions.filter(
      (q) => q.category === 'Part II' && Array.isArray(q.options)
    );
  } catch (err) {
    console.error('Error fetching Part II questions:', err);
    setError('Failed to load Part II questions. Please try again.');
    return;
  }

  // Filter Part II questions based on eligible taxonomies (subjects)
  let filteredPartIIQuestions = availablePartIIQuestions.filter(
    (q) => q.subCategory && allEligibleTaxonomies.includes(q.subCategory)
  );

  // Remove duplicates by question ID
  filteredPartIIQuestions = Array.from(
    new Map(filteredPartIIQuestions.map((q) => [q._id, q])).values()
  );

  const partIIAnsweredIds = answeredOrder.filter((id) =>
    filteredPartIIQuestions.some((q) => q._id === id)
  );
  const partIIAnsweredQuestions = partIIAnsweredIds
    .map((id) => filteredPartIIQuestions.find((q) => q._id === id))
    .filter((q) => q);
  const partIIUnanswered = filteredPartIIQuestions
    .filter((q) => !partIIAnsweredIds.includes(q._id))
    .sort(() => Math.random() - 0.5);

  const newQuestions = [...partIIAnsweredQuestions, ...partIIUnanswered];

  if (newQuestions.length === 0) {
    setError('No Part II questions available for the selected subjects.');
    return;
  }

  setQuestions(newQuestions);
  setHasProceededToPartII(true);

  let startIndex = 0;
  if (partIIAnsweredIds.length > 0) {
    const lastPartIIAnsweredId = partIIAnsweredIds[partIIAnsweredIds.length - 1];
    const lastPartIIAnsweredIndex = newQuestions.findIndex(
      (q) => q._id === lastPartIIAnsweredId
    );
    startIndex = lastPartIIAnsweredIndex >= 0 ? lastPartIIAnsweredIndex : 0;
  }

  setCurrentIndex(startIndex < newQuestions.length ? startIndex : 0);

  setShowPartICompletePage(true);
  setPartICompleteShown(true);

  // Fire confetti celebration
  const shootConfetti = () => {
    confetti({
      particleCount: 25,
      spread: 100,
      startVelocity: 35,
      decay: 0.9,
      gravity: 0.5,
      origin: { x: 0.5, y: 0.6 },
      shapes: ['star', 'circle'],
      colors: ['#ffdd00', '#ff6699', '#44ff88', '#ffffff', '#ff9933'],
      scalar: 1.4,
      ticks: 350,
    });
  };

  shootConfetti();
  setTimeout(shootConfetti, 500);
  setTimeout(shootConfetti, 1000);
  setTimeout(shootConfetti, 1800);

  try {
    setStoredData('Answers', selectedAnswers);
    setStoredData('Order', answeredOrder);
    setStoredData('Questions', newQuestions.map((q) => q._id));
    setStoredData('Index', startIndex);
    setStoredData('TopSubcategories', topSubcategoriesForPartII);
    setStoredData('HasProceededToPartII', true);
    setStoredData('SessionId', sessionId);
    setError('');
  } catch (err) {
    console.error('Error saving progress to local storage:', err);
    setError('Failed to save progress. Please try again.');
  }
};



  // Render loading, error, or empty states
  if (loading) return <p>Loading UniReady Assessment questions...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!questions.length) {
    console.log('questions is empty after loadAssessment:', questions);
    return (
      <p className="error-message">
        No UniReady Assessment questions available. Please contact support.
      </p>
    );
  }

  // Render assessment UI
  const currentQuestion = questions[currentIndex];
  const currentSelectedAnswer = selectedAnswers[currentQuestion._id] || '';
  const showPreviousButton = currentIndex > 0;

  return (
    <div className="specific-page-subject">
      <header className="assessment-header">
        <div className="admin-dashboard-logo logo-adjustment-score"></div>
        <h1 className="assessment-heading">ASPIRE UniReady Psychometric Test</h1>
        <p className="copyright">© 2026 Oxbridge Gateway Ltd., All Rights Reserved.</p>
                  <button className="nav-button-save" onClick={handleSaveProgress}>
              <FaSave className="save-icon" />
            </button>
      </header>

      <div className="assessment-container">
        <div className="progress-container">
          <p className="text-subject">
            Part II will focus on subjects from the two highest-ranked categories in Part I
          </p>
          <div className="progress-bar">
            <div
              className="progress-fill total-progress"
              style={{
                width: `${(answeredCount / questions.length) * 100}%`,
              }}
            ></div>
          </div>
        </div>

        <div className="assessment-question-container">
          <h3 className="category-text-subject">Category: {currentQuestion.category}</h3>
          <p className="category-text-subject-progress">
            Progress: {answeredCountByCategory[currentQuestion.category] || 0} /
            {questions.filter((q) => q.category === currentQuestion.category).length}{' '}
            questions
          </p>
          <p className="assessment-question-text-subject">
            {currentQuestion.questionText}
          </p>

          <div className="navigation-container">
            <div className="options-navigation">
              <button
                className={`nav-arrow nav-arrow-left ${showPreviousButton ? 'visible' : ''}`}
                onClick={handlePrevious}
                aria-label="Previous question"
                disabled={!showPreviousButton}
              >
                <svg viewBox="0 0 24 24" className="arrow-icon-subject">
                  <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
              <div className="options-container">
                {Array.isArray(currentQuestion.options) ? (
                  currentQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      className={`option-button ${
                        currentSelectedAnswer === option.optionText ? 'selected' : ''
                      }`}
                      onClick={() => handleAnswerSelect(option.optionText)}
                    >
                      {option.optionText}
                    </button>
                  ))
                ) : (
                  <p className="error-message">
                    No options available for this question.
                  </p>
                )}
              </div>
              <button
                className={`nav-arrow nav-arrow-right ${
                  currentIndex < questions.length - 1 ? 'visible' : ''
                }`}
                onClick={handleNext}
                aria-label="Next question"
                disabled={currentIndex >= questions.length - 1}
              >
                <svg viewBox="0 0 24 24" className="arrow-icon-subject">
                  <path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
            </div>
          </div>
          <div className="action-buttons">
            {(() => {
              console.log('Button render check:', {
                hasProceededToPartII,
                questions: questions.map((q) => ({ id: q._id, category: q.category })),
                selectedAnswers,
                partIIQuestions: questions.filter((q) => q.category === 'Part II'),
                partIIAnswered: questions
                  .filter((q) => q.category === 'Part II')
                  .every((q) => selectedAnswers[q._id] !== undefined),
              });

              if (!hasProceededToPartII) {
                const partIQuestions = questions.filter((q) => q.category === 'Part I');
                const partIAnswered = partIQuestions.every(
                  (q) => selectedAnswers[q._id] !== undefined
                );
                if (partIAnswered && partIQuestions.length > 0) {
                  return (
                    <button className="nav-button" onClick={handleSaveAndProceed}>
                      Proceed to Part II
                    </button>
                  );
                }
              } else {
                const partIIQuestions = questions.filter((q) => q.category === 'Part II');
                const partIIAnswered = partIIQuestions.every(
                  (q) => selectedAnswers[q._id] !== undefined
                );
                if (partIIAnswered && partIIQuestions.length > 0) {
                  return (
                    <button className="nav-button" onClick={handleAnswerSubmit}>
                      Submit
                    </button>
                  );
                }
              }
              return null;
            })()}
          </div>
        </div>
        <button
          className="return-dashboard-cross"
          onClick={handleReturnToDashboard}
        ></button>
      </div>
    </div>
  );
};


export default SubjectAssessment;